<template>
  <!-- Floating Statistics Section - Overlapping hero -->
  <div class="relative -mt-24 sm:-mt-32 z-40">
    <div class="container mx-auto px-4 sm:px-6">
      <div class="py-6 sm:py-8 px-6 sm:px-8 rounded-2xl sm:rounded-3xl shadow-2xl bg-white relative">
        <h2 class="text-2xl sm:text-3xl md:text-4xl font-bold mb-6 md:mb-8 text-primary text-center">Our Impact</h2>

        <!-- Statistics Grid -->
        <div ref="statsContainer" class="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 mb-6">
          <div
            v-for="stat in stats"
            :key="stat.label"
            class="text-center group"
          >
            <!-- Icon -->
            <div class="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-primary/10 flex items-center justify-center mb-3 mx-auto group-hover:bg-primary/20 transition-all duration-300">
              <component :is="stat.icon" class="w-7 h-7 sm:w-8 sm:h-8 text-primary" />
            </div>

            <!-- Animated Number -->
            <div class="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-1">
              {{ animatedValues[stat.label] }}{{ stat.suffix }}
            </div>

            <!-- Label -->
            <p class="text-xs sm:text-sm text-gray-900 font-medium">
              {{ stat.label }}
            </p>
          </div>
        </div>

        <!-- Bottom Action Buttons -->
        <div class="flex justify-center gap-4">
          <button
            @click="$router.push('/impact')"
            class="w-14 h-14 sm:w-16 sm:h-16 bg-primary hover:bg-accent rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 group"
            aria-label="View all programs"
          >
            <svg
              class="w-6 h-6 text-white group-hover:translate-x-1 transition-transform"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3"/>
            </svg>
          </button>
          <button
            @click="$router.push('/donate')"
            class="w-14 h-14 sm:w-16 sm:h-16 bg-accent hover:bg-primary rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 group"
            aria-label="Support our mission"
          >
            <svg
              class="w-6 h-6 text-white transition-transform"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"/>
            </svg>
          </button>
        </div>

      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component'
import { Users, Briefcase, Bird, Store, Heart } from 'lucide-vue-next'
import { analyticsData } from '@/store/analytics'

@Options({
  components: {
    Users,
    Briefcase,
    Bird,
    Store,
    Heart
  },
  data() {
    return {
      stats: analyticsData.slice(0, 4), // Show first 4 stats in CTA cards
      animatedValues: {} as Record<string, number>,
      hasAnimated: false
    }
  },
  mounted() {
    // Initialize animated values to 0
    this.stats.forEach((stat: any) => {
      this.animatedValues[stat.label] = 0
    })

    // Set up intersection observer for animation trigger
    this.$nextTick(() => {
      const element = this.$refs.statsContainer as Element
      if (element) {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting && !this.hasAnimated) {
                this.hasAnimated = true
                this.animateCounters()
              }
            })
          },
          { threshold: 0.3 }
        )
        observer.observe(element)
      }
    })
  },
  methods: {
    animateCounters() {
      this.stats.forEach((stat: any) => {
        this.animateValue(stat.label, 0, stat.value, 3500)
      })
    },
    animateValue(label: string, start: number, end: number, duration: number) {
      const startTime = performance.now()
      const animate = (currentTime: number) => {
        const elapsed = currentTime - startTime
        const progress = Math.min(elapsed / duration, 1)

        // Easing function for smooth, slower animation
        const easeOutCubic = 1 - Math.pow(1 - progress, 3)
        const current = Math.floor(start + (end - start) * easeOutCubic)

        this.animatedValues[label] = current

        if (progress < 1) {
          requestAnimationFrame(animate)
        } else {
          this.animatedValues[label] = end
        }
      }
      requestAnimationFrame(animate)
    }
  }
})
export default class CTACards extends Vue {}
</script>

<style scoped>
/* Ensure smooth transitions */
* {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
}
</style>
